import { createFileRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'

import { LoginForm } from '@/features/authentication/components/login-form'

const fallback = '/leave/calendar' as const

export const Route = createFileRoute('/login')({
  validateSearch: z.object({
    redirect: z.string().optional().catch(''),
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: search.redirect || fallback })
    }
  },
  component: Login,
})

function Login() {
  return (
    <div className="relative isolate flex min-h-screen flex-col overflow-hidden bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-slate-950 dark:via-emerald-950/40 dark:to-teal-900/30">
      <main className="relative z-10 grid flex-1 grid-cols-1 text-slate-900 dark:text-slate-100 lg:grid-cols-[1.05fr_0.95fr]">
        <section className="relative hidden flex-col justify-center gap-10 bg-gradient-to-br from-[#A5B595] via-[#9CAA84] to-[#8A9B74] px-12 py-16 text-white lg:flex xl:px-20">
          <div className="flex items-center gap-4">
            <img src="/images/logo/logo-light.png" alt="UIBFS" className="h-12 w-auto drop-shadow" />
            <div className="text-sm font-semibold uppercase tracking-[0.28em] text-primary/80">Leave Portal</div>
          </div>

          <div className="space-y-5">
            <h1 className="text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-[3.4rem] text-primary">UIBFS Leave Portal</h1>
            <p className="max-w-xl text-lg leading-relaxed text-primary/85">
              Seamless leave requests, approvals, and balance tracking for every team.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <svg aria-hidden="true" focusable="false" className="w-full text-white/65" viewBox="0 0 420 70" fill="none">
              <path
                d="M0 28c45 24 90-24 135 0s90 24 135 0 90-24 135 0"
                stroke="currentColor"
                strokeWidth="5"
                strokeLinecap="round"
                opacity="0.9"
                className="motion-safe:animate-[waveFloat_9s_ease-in-out_infinite]"
              />
              <path
                d="M0 44c45 24 90-24 135 0s90 24 135 0 90-24 135 0"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                opacity="0.6"
                className="motion-safe:animate-[waveFloat_9s_ease-in-out_infinite]"
                style={{ animationDelay: '0.6s' }}
              />
              <path
                d="M0 60c45 24 90-24 135 0s90 24 135 0 90-24 135 0"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                opacity="0.4"
                className="motion-safe:animate-[waveFloat_9s_ease-in-out_infinite]"
                style={{ animationDelay: '1.2s' }}
              />
            </svg>
          </div>
        </section>

        <section className="relative flex items-center justify-center bg-white/85 px-6 py-14 backdrop-blur-xl dark:bg-slate-950/70 lg:px-12 xl:px-18">
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/50 to-white/40 dark:from-slate-950/50 dark:via-slate-950/55 dark:to-slate-950/60" aria-hidden />
          <div className="relative z-10 w-full max-w-lg">
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              <img src="/images/logo/logo.png" alt="UIBFS" className="h-10 w-auto" />
              <span className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-700 dark:text-emerald-200">Leave Portal</span>
            </div>
            <LoginForm />
          </div>
        </section>
      </main>

      <footer className="pointer-events-none absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-center text-xs text-slate-700 dark:text-slate-300">
        <p className="pointer-events-auto">© 2025 UIBFS. All rights reserved.</p>
      </footer>
    </div>
  )
}
